import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { fetchTenants, updateTenant } from '../../../redux/slices/tenantSlice';
import { AppDispatch, RootState } from '../../../redux/store';
import { Card } from '@/components/ui/card';
import { SetTable } from '@/components/SetTable';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { 
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
    SheetFooter,
    SheetClose
} from '@/components/ui/sheet';

export default function TenantDetail() {
    const location = useLocation();
    const { tenantId } = useParams();
    const { tenant } = location.state || {};
    const { tenants, loading } = useSelector((state: RootState) => state.tenants);
    const dispatch = useDispatch<AppDispatch>();
    
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [updatedTenant, setUpdatedTenant] = useState(tenant);
    const [leaseStartDate, setLeaseStartDate] = useState<Date | undefined>(
        tenant?.leaseStartDate ? new Date(tenant.leaseStartDate) : undefined
    );
    const [leaseEndDate, setLeaseEndDate] = useState<Date | undefined>(
        tenant?.leaseEndDate ? new Date(tenant.leaseEndDate) : undefined
    );

    useEffect(() => {
        dispatch(fetchTenants({ building: tenant?.building?._id, flatNo: tenant?.flatNo, role: "Tenant" }));
    }, [dispatch, tenantId]);

    const handleSaveLease = async () => {
        if (!leaseStartDate || !leaseEndDate || !tenant?._id) {
            console.error('Missing required data for update');
            return;
        }

        try {
            const payload = {
                leaseStartDate: leaseStartDate.toISOString().split('T')[0],
                leaseEndDate: leaseEndDate.toISOString().split('T')[0]
            };

            const result = await dispatch(updateTenant({ 
                id: tenant._id, 
                payload 
            })).unwrap();

            // Update local tenant state with new dates
            const updatedTenantData = {
                ...tenant,
                leaseStartDate: payload.leaseStartDate,
                leaseEndDate: payload.leaseEndDate
            };
            setUpdatedTenant(updatedTenantData);
            
            setIsEditModalOpen(false);
            
            // Show success message (you can replace with toast notification)
            console.log('Lease dates updated successfully!', result);
            
        } catch (error) {
            console.error('Failed to update lease dates:', error);
            // Show error message (you can replace with toast notification)
        }
    };

    const handleCancelEdit = () => {
        setLeaseStartDate(updatedTenant?.leaseStartDate ? new Date(updatedTenant.leaseStartDate) : undefined);
        setLeaseEndDate(updatedTenant?.leaseEndDate ? new Date(updatedTenant.leaseEndDate) : undefined);
        setIsEditModalOpen(false);
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const tenantstableKeys = [
        {
            name: "name",
        },
        {
            username: "username",
        },
        {
            contact: "contactNumber",
        },
        {
            flatNo: "flatNo",
        },
    ];

    // Use updatedTenant for display, fallback to original tenant
    const displayTenant = updatedTenant || tenant;

    return (
        <div className="container mx-auto py-2 px-4 max-w-7xl">
            {/* Tenant Detail Card */}
            <Card className="border border-gray-200 shadow-lg p-4 mb-4">
                <div className="flex justify-between items-center mb-3">
                    <h1 className="text-xl font-bold text-gray-900">Tenant Details</h1>
                    <Badge 
                        variant={displayTenant?.isActive ? "default" : "secondary"}
                        className="text-xs px-2 py-0.5"
                    >
                        {displayTenant?.isActive ? "Active" : "Inactive"}
                    </Badge>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Personal Information Section */}
                    <div className="space-y-2">
                        <h3 className="text-sm font-semibold text-gray-800 mb-2 border-b pb-1">
                            Personal Information
                        </h3>
                        <div className="space-y-1.5">
                            <div className="flex flex-col">
                                <Label className="text-xs font-medium text-gray-600 mb-0">Full Name</Label>
                                <p className="text-sm text-gray-900 font-medium">{displayTenant?.name || "N/A"}</p>
                            </div>
                            <div className="flex flex-col">
                                <Label className="text-xs font-medium text-gray-600 mb-0">Username</Label>
                                <p className="text-sm text-gray-900">{displayTenant?.username || "N/A"}</p>
                            </div>
                            <div className="flex flex-col">
                                <Label className="text-xs font-medium text-gray-600 mb-0">Email</Label>
                                <p className="text-sm text-gray-900">{displayTenant?.email || "N/A"}</p>
                            </div>
                            <div className="flex flex-col">
                                <Label className="text-xs font-medium text-gray-600 mb-0">Phone</Label>
                                <p className="text-sm text-gray-900">{displayTenant?.contactNumber || "N/A"}</p>
                            </div>
                        </div>
                    </div>

                    {/* Property Information Section */}
                    <div className="space-y-2">
                        <h3 className="text-sm font-semibold text-gray-800 mb-2 border-b pb-1">
                            Property Information
                        </h3>
                        <div className="space-y-1.5">
                            <div className="flex flex-col">
                                <Label className="text-xs font-medium text-gray-600 mb-0">Building</Label>
                                <p className="text-sm text-gray-900 font-medium">{displayTenant?.building?.name || "N/A"}</p>
                            </div>
                            <div className="flex flex-col">
                                <Label className="text-xs font-medium text-gray-600 mb-0">Flat Number</Label>
                                <p className="text-sm text-gray-900 font-medium">{displayTenant?.flatNo || "N/A"}</p>
                            </div>
                            <div className="flex flex-col">
                                <Label className="text-xs font-medium text-gray-600 mb-0">Status</Label>
                                <p className="text-sm text-gray-900">{displayTenant?.status || "N/A"}</p>
                            </div>
                        </div>
                    </div>

                    {/* Lease Information with Edit Functionality */}
                    <div className="space-y-2">
                        <h3 className="text-sm font-semibold text-gray-800 mb-2 border-b pb-1">
                            Lease Information
                        </h3>

                        <div className="space-y-1.5">
                            <div className="flex flex-col">
                                <Label className="text-xs font-medium text-gray-600 mb-0">Start Date</Label>
                                <p className="text-sm text-gray-900">{formatDate(displayTenant?.leaseStartDate) || "N/A"}</p>
                            </div>
                            <div className="flex flex-col">
                                <Label className="text-xs font-medium text-gray-600 mb-0">End Date</Label>
                                <p className="text-sm text-gray-900">{formatDate(displayTenant?.leaseEndDate) || "N/A"}</p>
                            </div>
                            
                            <div className="pt-2">
                                <Sheet open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
                                    <SheetTrigger asChild>
                                        <Button 
                                            variant="outline" 
                                            size="sm"
                                            className="h-6 px-2 text-xs w-full"
                                            disabled={loading}
                                        >
                                            Edit Lease Dates
                                        </Button>
                                    </SheetTrigger>
                                    <SheetContent className="w-[700px] sm:max-w-[700px]">
                                        <SheetHeader className="pb-6 border-b">
                                            <SheetTitle className="text-2xl font-bold text-gray-900">Edit Lease Dates</SheetTitle>
                                            <SheetDescription className="text-gray-600 mt-2">
                                                Update the lease start and end dates for <span className="font-semibold text-gray-800">{displayTenant?.name}</span>
                                            </SheetDescription>
                                        </SheetHeader>
                                        
                                        <div className="py-8">
                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                                {/* Lease Start Date */}
                                                <div className="space-y-4">
                                                    <div className="space-y-2">
                                                        <Label className="text-base font-semibold text-gray-800">
                                                            Lease Start Date
                                                        </Label>
                                                        {leaseStartDate && (
                                                            <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                                                <span className="text-sm font-medium text-blue-800">
                                                                    Selected: {formatDate(leaseStartDate.toISOString())}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm hover:shadow-md transition-shadow">
                                                        <Calendar
                                                            mode="single"
                                                            selected={leaseStartDate}
                                                            onSelect={setLeaseStartDate}
                                                            className="rounded-lg"
                                                            classNames={{
                                                                months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                                                                month: "space-y-4",
                                                                caption: "flex justify-center pt-1 relative items-center",
                                                                caption_label: "text-sm font-medium",
                                                                nav: "space-x-1 flex items-center",
                                                                nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
                                                                nav_button_previous: "absolute left-1",
                                                                nav_button_next: "absolute right-1",
                                                                table: "w-full border-collapse space-y-1",
                                                                head_row: "flex",
                                                                head_cell: "text-gray-500 rounded-md w-9 font-normal text-[0.8rem]",
                                                                row: "flex w-full mt-2",
                                                                cell: "text-center text-sm p-0 relative [&:has([aria-selected])]:bg-blue-100 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                                                                day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-gray-100 rounded-md transition-colors",
                                                                day_selected: "bg-blue-600 text-white hover:bg-blue-700 focus:bg-blue-600 focus:text-white font-semibold ring-2 ring-blue-200",
                                                                day_today: "bg-transparent text-gray-900 font-normal",
                                                                day_outside: "text-gray-400 opacity-50",
                                                                day_disabled: "text-gray-400 opacity-50",
                                                                day_range_middle: "aria-selected:bg-gray-100 aria-selected:text-gray-900",
                                                                day_hidden: "invisible",
                                                            }}
                                                            initialFocus
                                                            defaultMonth={leaseStartDate}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Lease End Date */}
                                                <div className="space-y-4">
                                                    <div className="space-y-2">
                                                        <Label className="text-base font-semibold text-gray-800">
                                                            Lease End Date
                                                        </Label>
                                                        {leaseEndDate && (
                                                            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                                                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                                                <span className="text-sm font-medium text-green-800">
                                                                    Selected: {formatDate(leaseEndDate.toISOString())}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm hover:shadow-md transition-shadow">
                                                        <Calendar
                                                            mode="single"
                                                            selected={leaseEndDate}
                                                            onSelect={setLeaseEndDate}
                                                            className="rounded-lg"
                                                            classNames={{
                                                                months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                                                                month: "space-y-4",
                                                                caption: "flex justify-center pt-1 relative items-center",
                                                                caption_label: "text-sm font-medium",
                                                                nav: "space-x-1 flex items-center",
                                                                nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
                                                                nav_button_previous: "absolute left-1",
                                                                nav_button_next: "absolute right-1",
                                                                table: "w-full border-collapse space-y-1",
                                                                head_row: "flex",
                                                                head_cell: "text-gray-500 rounded-md w-9 font-normal text-[0.8rem]",
                                                                row: "flex w-full mt-2",
                                                                cell: "text-center text-sm p-0 relative [&:has([aria-selected])]:bg-green-100 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                                                                day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-gray-100 rounded-md transition-colors",
                                                                day_selected: "bg-green-600 text-white hover:bg-green-700 focus:bg-green-600 focus:text-white font-semibold ring-2 ring-green-200",
                                                                day_today: "bg-transparent text-gray-900 font-normal",
                                                                day_outside: "text-gray-400 opacity-50",
                                                                day_disabled: "text-gray-400 opacity-50",
                                                                day_range_middle: "aria-selected:bg-gray-100 aria-selected:text-gray-900",
                                                                day_hidden: "invisible",
                                                            }}
                                                            disabled={(date) => leaseStartDate ? date < leaseStartDate : false}
                                                            defaultMonth={leaseEndDate}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <SheetFooter className="border-t pt-6 flex flex-row justify-end gap-3">
                                            <SheetClose asChild>
                                                <Button 
                                                    variant="outline" 
                                                    onClick={handleCancelEdit}
                                                    className="px-6 py-2 text-sm font-medium border-gray-300 text-gray-700 hover:bg-gray-50"
                                                    disabled={loading}
                                                >
                                                    Cancel
                                                </Button>
                                            </SheetClose>
                                            <Button 
                                                onClick={handleSaveLease}
                                                className="px-6 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
                                                disabled={!leaseStartDate || !leaseEndDate || loading}
                                            >
                                                {loading ? "Saving..." : "Save Changes"}
                                            </Button>
                                        </SheetFooter>
                                    </SheetContent>
                                </Sheet>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Same Flat Tenants Card */}
            <Card className="border border-gray-200 shadow-lg p-4">
                <h2 className="text-lg font-bold text-gray-900 mb-3 border-b pb-1">
                    Other Tenants in Flat {displayTenant?.flatNo}
                </h2>
                <div className="bg-gray-50 rounded p-2">
                    <SetTable data={tenants} tableKeys={tenantstableKeys} />
                </div>
            </Card>
        </div>
    )
}
